import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import DataTable from '../../components/ui/data-table';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { useAttendance } from '../../hooks/useAttendance';
import { attendanceService } from '../../services/attendanceService';
import { 
  Calendar, 
  CheckCircle, 
  XCircle, 
  Clock, 
  TrendingUp,
  AlertCircle,
  RefreshCw,
  BarChart3,
  CalendarDays
} from 'lucide-react';

const AttendancePage = () => {
  const { attendance, loading, error, fetchAttendance } = useAttendance();
  // const [stats, setStats] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const response = await attendanceService.getAttendanceStats();
      // setStats(response.data.data);
    } catch (err) {
      console.error('Stats fetch error:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchCalendar = async () => {
    try {
      setLoadingCalendar(true);
      const response = await attendanceService.getMyAttendanceCalendar(selectedYear, selectedMonth);
      setCalendar(response.data.data);
    } catch (err) {
      console.error('Calendar fetch error:', err);
    } finally {
      setLoadingCalendar(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchCalendar();
  }, [selectedMonth, selectedYear]);

  const columns = [
    { 
      id: 'date', 
      header: 'Date', 
      accessorKey: 'date', 
      sortable: true,
      cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          <Calendar className="h-4 w-4 text-gray-500" />
          <span>{new Date(row.original.date).toLocaleDateString()}</span>
        </div>
      )
    },
    { 
      id: 'class', 
      header: 'Class', 
      accessorKey: 'className',
      cell: ({ row }) => (
        <Badge variant="outline">{row.original.className || 'N/A'}</Badge>
      )
    },
    { 
      id: 'status', 
      header: 'Status', 
      accessorKey: 'status', 
      sortable: true,
      cell: ({ row }) => {
        const status = row.original.status;
        const variants = {
          'present': { variant: 'default', icon: CheckCircle, color: 'text-green-600' },
          'absent': { variant: 'destructive', icon: XCircle, color: 'text-red-600' },
          'late': { variant: 'secondary', icon: Clock, color: 'text-orange-600' },
          'excused': { variant: 'outline', icon: AlertCircle, color: 'text-blue-600' }
        };
        const config = variants[status] || variants['absent'];
        const Icon = config.icon;
        return (
          <Badge variant={config.variant} className="flex items-center space-x-1">
            <Icon className={`h-3 w-3 ${config.color}`} />
            <span className="capitalize">{status}</span>
          </Badge>
        );
      }
    },
    { 
      id: 'time', 
      header: 'Time', 
      accessorKey: 'timeIn',
      cell: ({ row }) => row.original.timeIn ? (
        <div className="flex items-center space-x-1">
          <Clock className="h-4 w-4 text-gray-500" />
          <span className="text-sm">{row.original.timeIn}</span>
        </div>
      ) : (
        <span className="text-gray-400">-</span>
      )
    },
    { 
      id: 'remarks', 
      header: 'Remarks', 
      accessorKey: 'remarks',
      cell: ({ row }) => row.original.remarks ? (
        <span className="text-sm text-gray-600">{row.original.remarks}</span>
      ) : (
        <span className="text-gray-400">-</span>
      )
    }
  ];

  const getAttendanceStats = () => {
    // Ensure attendance is an array
    const attendanceArray = Array.isArray(attendance) ? attendance : [];
    
    if (attendanceArray.length === 0) return null;

    const totalDays = attendanceArray.length;
    const presentDays = attendanceArray.filter(a => a.status === 'present').length;
    const absentDays = attendanceArray.filter(a => a.status === 'absent').length;
    const lateDays = attendanceArray.filter(a => a.status === 'late').length;
    const excusedDays = attendanceArray.filter(a => a.status === 'excused').length;
    
    const attendanceRate = (presentDays / totalDays) * 100;
    const punctualityRate = ((presentDays - lateDays) / totalDays) * 100;

    return {
      totalDays,
      presentDays,
      absentDays,
      lateDays,
      excusedDays,
      attendanceRate,
      punctualityRate
    };
  };

  const localStats = getAttendanceStats();

  const renderCalendar = () => {
    // Ensure calendar is an array
    const calendarArray = Array.isArray(calendar) ? calendar : [];
    
    if (!calendarArray.length && !loadingCalendar) return <div>No calendar data available</div>;
    if (loadingCalendar) return <div>Loading calendar...</div>;

    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    const firstDay = new Date(selectedYear, selectedMonth - 1, 1).getDay();
    
    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      const dayAttendance = calendarArray.find(a => a.date === dateStr);
      
      let statusColor = 'bg-gray-100';
      let statusIcon = null;
      
      if (dayAttendance) {
        switch (dayAttendance.status) {
          case 'present':
            statusColor = 'bg-green-100';
            statusIcon = <CheckCircle className="h-3 w-3 text-green-600" />;
            break;
          case 'absent':
            statusColor = 'bg-red-100';
            statusIcon = <XCircle className="h-3 w-3 text-red-600" />;
            break;
          case 'late':
            statusColor = 'bg-orange-100';
            statusIcon = <Clock className="h-3 w-3 text-orange-600" />;
            break;
          case 'excused':
            statusColor = 'bg-blue-100';
            statusIcon = <AlertCircle className="h-3 w-3 text-blue-600" />;
            break;
        }
      }
      
      days.push(
        <div key={day} className={`p-2 border rounded text-center ${statusColor}`}>
          <div className="text-sm font-medium">{day}</div>
          {statusIcon}
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-7 gap-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} className="p-2 text-center font-medium text-gray-500 text-sm">
            {day}
          </div>
        ))}
        {days}
      </div>
    );
  };

  if (loading) return <LoadingSpinner />;
  
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <div className="text-red-600 text-sm mb-4">{String(error)}</div>
            <Button onClick={() => fetchAttendance()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">My Attendance</h2>
          <p className="text-gray-600 mt-1">Track your attendance records and statistics</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => fetchAttendance({ month: 'current' })}>
            This Month
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchAttendance({ month: 'previous' })}>
            Last Month
          </Button>
          <Button variant="outline" size="sm" onClick={() => fetchAttendance()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      {localStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Attendance Rate</p>
                  <p className="text-2xl font-semibold">{localStats.attendanceRate.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Punctuality Rate</p>
                  <p className="text-2xl font-semibold">{localStats.punctualityRate.toFixed(1)}%</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Late Arrivals</p>
                  <p className="text-2xl font-semibold">{localStats.lateDays}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Absent Days</p>
                  <p className="text-2xl font-semibold">{localStats.absentDays}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="records" className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="records">Records</TabsTrigger>
          <TabsTrigger value="calendar">Calendar View</TabsTrigger>
          <TabsTrigger value="statistics">Statistics</TabsTrigger>
        </TabsList>

        <TabsContent value="records">
          {(!attendance || !Array.isArray(attendance) || attendance.length === 0) ? (
            <EmptyState 
              icon={Calendar}
              title="No attendance records"
              description="Your attendance records will appear here once they are recorded."
              action
              actionLabel="Refresh"
              onAction={() => fetchAttendance()}
            />
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5" />
                  <span>Attendance Records ({attendance.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <DataTable 
                  data={attendance} 
                  columns={columns} 
                  searchable 
                  searchPlaceholder="Search attendance records..."
                />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="calendar">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <CalendarDays className="h-5 w-5" />
                  <span>Attendance Calendar</span>
                </div>
                <div className="flex items-center space-x-2">
                  <select 
                    value={selectedMonth} 
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="px-3 py-1 border rounded text-sm"
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {new Date(0, i).toLocaleString('default', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                  <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="px-3 py-1 border rounded text-sm"
                  >
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = new Date().getFullYear() - 2 + i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingCalendar ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : (
                <div>
                  {renderCalendar()}
                  <div className="mt-4 flex items-center justify-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>Present</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <XCircle className="h-4 w-4 text-red-600" />
                      <span>Absent</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="h-4 w-4 text-orange-600" />
                      <span>Late</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <AlertCircle className="h-4 w-4 text-blue-600" />
                      <span>Excused</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="statistics">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <BarChart3 className="h-5 w-5" />
                <span>Detailed Statistics</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingStats ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : localStats ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded">
                      <div className="text-2xl font-bold text-gray-900">{localStats.totalDays}</div>
                      <div className="text-sm text-gray-600">Total Days</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded">
                      <div className="text-2xl font-bold text-green-600">{localStats.presentDays}</div>
                      <div className="text-sm text-gray-600">Present</div>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded">
                      <div className="text-2xl font-bold text-red-600">{localStats.absentDays}</div>
                      <div className="text-sm text-gray-600">Absent</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded">
                      <div className="text-2xl font-bold text-orange-600">{localStats.lateDays}</div>
                      <div className="text-sm text-gray-600">Late</div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded">
                      <h4 className="font-medium mb-2">Attendance Rate</h4>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full" 
                          style={{ width: `${localStats.attendanceRate}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{localStats.attendanceRate.toFixed(1)}%</p>
                    </div>
                    
                    <div className="p-4 border rounded">
                      <h4 className="font-medium mb-2">Punctuality Rate</h4>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${localStats.punctualityRate}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{localStats.punctualityRate.toFixed(1)}%</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No statistics available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AttendancePage;